import { LucideIcon } from 'lucide-react'
import React from 'react'
import { cn } from '../../lib/utils'

export interface UnifiedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom'
  icon?: LucideIcon
  iconPosition?: 'left' | 'right'
  iconSize?: number
  iconColor?: string
  loading?: boolean
  fullWidth?: boolean
  children?: React.ReactNode
}

const variantStyles = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow dark:shadow-none',
  secondary:
    'bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border shadow-sm dark:shadow-none',
  outline:
    'border border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-sm dark:shadow-none',
  ghost: 'hover:bg-accent hover:text-accent-foreground transition-colors',
  destructive:
    'border border-input bg-background text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/20 shadow-sm dark:shadow-none'
}

const sizeStyles = {
  xs: 'h-6 px-2 text-xs gap-1 touch-target-sm',
  sm: 'h-8 px-3 text-sm gap-1.5 touch-target-sm',
  md: 'h-10 px-4 text-sm gap-2 touch-target',
  lg: 'h-11 px-6 text-base gap-2 touch-target',
  xl: 'h-12 px-8 text-lg gap-2.5',
  custom: ''
}

const iconSizes = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  xl: 'h-6 w-6',
  custom: 'h-4 w-4'
}

export const UnifiedButton = React.forwardRef<HTMLButtonElement, UnifiedButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      icon: Icon,
      iconPosition = 'left',
      iconColor = 'currentColor',
      loading = false,
      fullWidth = false,
      iconSize = 4,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        className={cn(
          // Base styles
          'inline-flex items-center justify-center rounded-md font-medium transition-colors',
          'focus-visible:outline-none',
          'disabled:pointer-events-none disabled:opacity-50',
          'cursor-pointer', // Always show pointer cursor when enabled

          // Variant styles
          variantStyles[variant],

          // Size styles
          sizeStyles[size],

          // Full width
          fullWidth && 'w-full',

          className
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {/* Left icon */}
        {Icon && iconPosition === 'left' && !loading && (
          <Icon className={cn(iconSizes[size] || iconSizes.md, iconColor)} />
        )}

        {/* Loading spinner */}
        {loading && (
          <div
            className={cn(
              'animate-spin rounded-full border-2 border-current border-t-transparent',
              iconSizes[size] || iconSizes.md
            )}
          />
        )}

        {/* Button Content */}
        {children && (
          <div className={cn(loading && 'opacity-0', 'flex w-full items-center justify-between')}>
            {children}
          </div>
        )}

        {/* Right icon */}
        {Icon && iconPosition === 'right' && !loading && (
          <Icon className={cn(`size-${iconSize}`)} />
        )}
      </button>
    )
  }
)

UnifiedButton.displayName = 'UnifiedButton'
